<template xmlns="http://www.w3.org/1999/html">
    <v-data-table
        :headers="headers"
        :items="items"
        :group-by="groupBy"
        :group-desc="true"
        fixed-header
        height="80vh"
        disable-pagination
        hide-default-footer
    >
        <template #top>
            <ActivitiesModal/>
            <slot name="top"></slot>
        </template>

        <template v-slot:group.header="{items, isOpen, toggle}">
            <th @click="toggle" colspan="99">
                <v-icon class="collapse" transition="fab-transition"
                >{{ isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
                {{ `${items[0][groupLabel]} - ${items.length} változás - ${lastChange(items)}` }}
            </th>
        </template>

        <template #item.subject="{ item }">
            {{ item.subject.title || item.subject.name }}
        </template>

        <template #item.effective_date="{ item }">
            {{ item.effective_date | moment('YYYY.MM.DD') }}
        </template>

        <template #item.date_of_repeal="{ item }">
            {{ (item.valid_until_revoked ? "Visszavonásig hatályos" : item.date_of_repeal) | moment("YYYY.MM.DD") }}
        </template>

        <template #item.is_public="{ item }">
            <v-icon>
                {{
                    item.is_public
                        ? 'mdi-check-circle-outline'
                        : 'mdi-close-circle-outline'
                }}
            </v-icon>
        </template>

        <template #item.is_archived="{ item }">
            <v-icon>
                {{
                    item.is_archived
                        ? 'mdi-check-circle-outline'
                        : 'mdi-close-circle-outline'
                }}
            </v-icon>
        </template>

        <template #item.is_active="{ item }">
            <v-icon :color="_color(item.is_active)">
                {{
                    item.is_active
                        ? 'mdi-check-circle-outline'
                        : 'mdi-close-circle-outline'
                }}
            </v-icon>
        </template>

        <template #item.is_incognito="{ item }">
            <v-icon>
                {{
                    item.is_incognito
                        ? 'mdi-check-circle-outline'
                        : 'mdi-close-circle-outline'
                }}
            </v-icon>
        </template>

        <template #item.is_internal="{ item }">
            <v-icon>
                {{
                    item.is_internal
                        ? 'mdi-check-circle-outline'
                        : 'mdi-close-circle-outline'
                }}
            </v-icon>
        </template>

        <template #item.valid_until_revoked="{ item }">
            <v-icon>
                {{
                    item.valid_until_revoked
                        ? 'mdi-check-circle-outline'
                        : 'mdi-close-circle-outline'
                }}
            </v-icon>
        </template>

        <template #item.role="{ item }">
            <v-chip :color="_chipColor(item.role.name)" outlined pill>
                {{ item.role.name || 'Nincs szerepkör' }}
            </v-chip>
        </template>

        <template #item.permissions="{ item }">
            <v-tooltip top max-width="600" :disabled="item.permissions.length == 0">
                <template #activator="{ on, attrs }">
                    <v-chip
                        outlined
                        pill
                        :color="item.permissions.length > 0 ? 'primary' : 'red'"
                        v-bind="attrs"
                        v-on="on"
                    >
                        Jogosultságok
                    </v-chip>
                </template>

                <template v-for="permission in item.permissions">
                    <p class="ma-0" :key="permission.id">
                        {{ permission.name }}
                    </p>
                </template>
            </v-tooltip>
        </template>

        <template #item.subject_type="{ item }">
            {{ $t(item.subject_type) }}
        </template>

        <template #item.description="{ item }">
            {{ $t(item.description) }}
        </template>

        <template #item.old="{ item }">
            <table>
                <tr v-for="(value, key) in item.old" v-if="item.old[key] !== item.new[key]">
                    <td class="px-3">{{ $t(key) }}</td>
                    <td v-if="key === 'file_path'" class="px-3">
                        <a :href="`/storage/documents/${value}`">{{ formatLong(value, key) }}</a>
                    </td>
                    <td v-else class="px-3">
                        {{ $t(key) }}
                    </td>
                    <td v-else class="px-3">{{ $t(formatDates(value, key)) }}</td>
                </tr>
            </table>
        </template>

        <template #item.new="{ item }">
            <table>
                <tr v-for="(value, key) in item.new" v-if="item.old[key] !== item.new[key]">
                    <td v-if="key === 'file_path'" class="px-3">
                        <a :href="`/storage/documents/${value}`">{{ formatLong(value, key) }}</a>
                    </td>
                    <td v-else class="px-3">
                        {{ $t(key) }}
                    </td>
                    <td class="px-3">{{ $t(formatDates(value, key)) }}</td>
                </tr>
            </table>
        </template>

        <template #item.actions="{ item }">
            <v-btn
                v-if="userHasPermission(`update.${model}`)"
                icon
                color="green"
                @click="handleEditItem(item)"
            >
                <v-icon>
                    mdi-pencil
                </v-icon>
            </v-btn>

            <v-btn
                v-if="userHasPermission(`read.${model}`) && (model === 'activities' || model==='gazettes')"
                @click="getItemActivity(item, model)"
                icon
                color="green"
            >
                <v-icon>
                    mdi-math-log
                </v-icon>
            </v-btn>

            <v-btn
                v-if="userHasPermission(`delete.${model}`)"
                icon
                color="red"
                @click="handleAlertModal(item)"
            >
                <v-icon>
                    mdi-trash-can
                </v-icon>
            </v-btn>
        </template>

        <template #footer>
            <Pagination @handlePageChanged="handlePageChanged"/>
        </template>
    </v-data-table>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { SET_ALERT_MODAL, SET_CALLBACK, SET_ITEM, SET_MODULE } from '../../utils/constants'

export default {
    props: {
        headers: {
            type: Array,
            default: () => []
        },
        items: {
            type: Array,
            default: () => []
        },
        groupBy: {
            type: String,
            default: null
        },
        groupLabel: {
            type: String,
            default: ""
        },
        componentTitle: {
            type: String,
            default: "",
            note: "Determines the actions can be performed to this component"
        },
        deleteCallback: {
            type: String,
            default: ""
        },
        module: {
            type: String,
            default: ""
        },
        model: {
            type: String,
            required: true
        }
    },
    mounted() {
        this.toggleAll();
    },
    computed: {
        ...mapGetters("Modal", ["item"]),
        ...mapGetters("Auth", ["userHasPermission"]),
        _chipColor() {
            return roleName => (roleName ? "primary" : "red");
        },
        _color() {
            return item => (item ? "green" : "red");
        },
    },
    updated() {
        this.toggleAll();
    },
    methods: {
        ...mapMutations("Modal", [
            SET_ITEM,
            SET_ALERT_MODAL,
            SET_MODULE,
            SET_CALLBACK
        ]),
        ...mapActions(["showActivities"]),
        handlePageChanged() {
            this.$emit("handlePageChanged");
        },
        handleEditItem(item) {
            this.$emit("handleEditItem", item);
        },
        getItemActivity(item, model) {
            if (model === "activities"){
                this.showActivities({id:item.id});
            }
            this.$emit("handleOldDocument", item);
        },
        handleAlertModal(item) {
            this[SET_MODULE](this.module);
            this[SET_CALLBACK](this.deleteCallback);
            this[SET_ALERT_MODAL](true);
            this[SET_ITEM](item);
        },
        handleDeleteItem() {
            this.$emit("handleDeleteItem", this.item);
        },
        toggleAll() {
            [...document.getElementsByClassName("collapse")].forEach(element => {
                    element.click()
                }
            );
        },
        formatDates(value, key) {
            const dates = ["created_at", "updated_at", "effective_date", "date_of_repeal"];
            if (dates.includes(key)) {
              try{
                return new Date(value).toISOString().replace("T", " ").split('.')[0];
              }catch (e){
                return value
              }
            }
            return value;
        },
        formatLong (value, key) {
          if (value.length < 37) {
            return value
          }
          return value.substring(0, 25) + '...' + value.substring(value.length - 12, value.length)
        },
        lastChange(items) {
            let dates = [];
            items.forEach(item => {
                dates.push(new Date(item.created_at));
            })
            return this.formatDates(Math.max(...dates), "created_at");
        },
    }
};
</script>
